import classNames from "classnames";
import { ReactNode, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useWebsiteButton } from "../../customeHooks/useWebsiteButtons";
import { authSliceActions } from "../../redux/features/auth/auth.slice";
import { useAppDispatch } from "../../redux/hooks";
import { clientAuthTokenCookieKey } from "../../utils/constants";
import { getCookie } from "../../utils/helpers";
import { Button } from "../buttons/button";
import { Icon } from "../icon/icon";
import { ScrollToTop } from "../scrollToTop";
import { ProfileDropdown } from "./appLayout";

const navs = [
  {
    title: "Home",
    to: "/",
  },
  {
    title: "Find Events",
    to: "/find-events",
  },
  {
    title: "Pricing",
    to: "/pricing",
  },
];

const footerNavs = [
  {
    title: "Privacy Policy",
    to: "/legal/privacy-policy",
  },
  {
    title: "Terms Of Use",
    to: "/legal/terms-of-use",
  },
  {
    title: "Refund Policy",
    to: "/legal/refund-policy",
  },
];

export const Layout = ({
  children,
  footerClassName = "",
}: {
  children: ReactNode;
  footerClassName?: string;
}) => {
  const [navOpen, setNavOpen] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(
    !!getCookie(clientAuthTokenCookieKey),
  );

  const container = useRef<HTMLDivElement>(null);

  const { goToLogin, goToSignup, goToProfile, goToDashboard } =
    useWebsiteButton();

  const dispatch = useAppDispatch();

  const dropDownOptions = [
    {
      icon: "share",
      label: "Go to Dashboard",
      onClick: () => {
        goToDashboard();
      },
    },
    {
      icon: "avatar",
      label: "View Profile",
      onClick: () => {
        goToProfile();
      },
    },
    {
      icon: "logout",
      label: "Logout",
      onClick: () => {
        dispatch(authSliceActions.signUserOut());

        setIsSignedIn(false);
      },
    },
  ];

  return (
    <>
      <ScrollToTop ref={container} />
      <div
        ref={container}
        className="flex h-screen max-h-[100dvh] flex-col overflow-scroll bg-light-grey-bg"
      >
        <div
          className={classNames(
            "sticky top-0 z-[999] w-full bg-light-grey-bg/[98]",
          )}
        >
          <div
            className={classNames(
              "max-width flex flex-col overflow-hidden",
              {
                "h-screen max-h-[100dvh] overflow-y-scroll": navOpen,
              },
              "transition-all duration-500",
            )}
          >
            <div className="relative flex items-center justify-between py-6 lg:justify-center">
              <Link
                to={"/"}
                className="max-w-[130px] lg:absolute lg:left-0 lg:max-w-max"
              >
                <img
                  src="/assets/logo_beta.svg"
                  className="w-full"
                  alt="logo"
                />
              </Link>

              <div className="hidden items-center text-base font-semibold text-grey-800 lg:flex">
                {navs.map((nav, idx) => {
                  return (
                    <NavLink
                      to={nav.to}
                      key={`${idx}`}
                      className={({ isActive }) => {
                        return `${isActive ? "bg-grey-150" : ""} rounded-[200px] px-3 py-2.5 transition-all duration-300`;
                      }}
                    >
                      {nav.title}
                    </NavLink>
                  );
                })}
              </div>

              <div className="lg:absolute lg:right-0 lg:max-w-max">
                {isSignedIn ? (
                  <ProfileDropdown
                    dropDownOptions={dropDownOptions}
                    useCookieProfile
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      className="w-fit lg:hidden"
                      onClick={() => {
                        setNavOpen(!navOpen);
                      }}
                    >
                      <Icon
                        svg={navOpen ? "close-icon" : "hamburger"}
                        width={20}
                        height={20}
                        className="transition-all duration-500"
                      />
                    </button>

                    <div className="hidden items-center gap-3 lg:flex">
                      <Button
                        type="button"
                        className="!max-w-fit !px-5"
                        text="Login"
                        variation="plain-grey"
                        onClick={() => {
                          goToLogin();
                        }}
                      />
                      <Button
                        type="button"
                        className="!max-w-fit !px-5"
                        text="Create An Event"
                        variation="primary"
                        onClick={() => {
                          goToSignup();
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {navOpen ? (
              <div className="relative flex flex-col gap-3 py-6 marker:flex-1">
                <img
                  src="/assets/images/sparkles.png"
                  alt="sparkles"
                  className="absolute bottom-[-10px] left-[-20px] max-w-[32px]"
                />
                <img
                  src="/assets/images/ticket.png"
                  alt="sparkles"
                  className="absolute right-0 top-0 max-w-[32px]"
                />
                {navs.map((nav, idx) => {
                  return (
                    <NavLink
                      to={nav.to}
                      key={`${idx}`}
                      className={({ isActive }) => {
                        return `max-w-fit text-grey-800 ${isActive ? "bg-grey-150" : ""} rounded-[200px] px-3 py-2.5 transition-all duration-300`;
                      }}
                      onClick={() => {
                        setNavOpen(false);
                      }}
                    >
                      {nav.title}
                    </NavLink>
                  );
                })}

                <div className="mt-[44px] flex flex-col items-center gap-3">
                  <Button
                    type="button"
                    className=""
                    text="Login"
                    variation="plain-grey"
                    onClick={() => {
                      goToLogin();
                    }}
                  />
                  <Button
                    type="button"
                    className=""
                    text="Create An Event"
                    variation="primary"
                    onClick={() => {
                      goToSignup();
                    }}
                  />
                </div>
              </div>
            ) : undefined}
          </div>
        </div>

        <div className="relative flex w-full flex-1">{children}</div>

        <div
          className={classNames(
            "border-t border-grey-100 bg-light-grey-bg/80 py-6",
            footerClassName,
          )}
        >
          <div className="max-width flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-sm font-semibold text-grey-800">©Tikket 2025</p>

            <div className="flex flex-row items-center gap-6">
              {footerNavs.map((nav, idx) => {
                return (
                  <NavLink
                    key={`${idx}`}
                    to={nav.to}
                    className={`text-sm font-semibold text-grey-800 underline`}
                  >
                    {nav.title}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
